import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { clearUser } from "@/integrations/datadog";
import { trackAction } from "@/integrations/datadog/track";
import type { AuthSession } from "@/lib/backend";
import { peekBackendConfig } from "@/lib/backend-config";
import { insforge } from "@/lib/insforge";
import { getQueryClient } from "@/lib/query-client";
import {
  cancelPasswordSessionRestores,
  PasswordSessionRestoreCancelledError,
  restorePasswordSession,
  signOutPasswordSession,
  StaffProfileLookupError,
} from "../api/password-auth";
import { isApprovedProfile, StaffSessionError } from "../approval";
import type {
  AuthContextValue,
  AuthErrorReason,
  AuthProfile,
  StaffSessionOrigin,
  StaffSessionResult,
} from "../types";
import { AuthContext, useAuthContext } from "./auth-context";
import { createRefreshRunner } from "./refresh-runner";
import { removeStoredSmsSession } from "./sms-session-storage";
import { useCompleteStaffSignIn } from "./use-complete-staff-sign-in";
import { isSmsTokenExpired, useSmsSessionExpiry } from "./use-sms-session-expiry";

const TOKEN_REFRESH_MS = 10 * 60 * 1000;
const FOCUS_FRESHNESS_MS = 60_000;
const FOCUS_RETRY_MS = 30_000;

function restoreStaffSession(
  origin: StaffSessionOrigin | null,
  expectedAuthUserId: string | null,
  isCurrent: () => boolean,
): Promise<StaffSessionResult | null> {
  if (origin === "sms") {
    return Promise.resolve(null);
  }
  return restorePasswordSession(expectedAuthUserId ?? undefined, isCurrent);
}

function handleSmsRefresh(input: {
  readonly clearAuthState: (reason: AuthErrorReason | null) => void;
  readonly expiresAt: number | null;
  readonly hasAuthState: boolean;
  readonly origin: StaffSessionOrigin | null;
}): boolean {
  if (input.origin !== "sms" || !input.hasAuthState) {
    return false;
  }
  if (isSmsTokenExpired(input.expiresAt)) {
    input.clearAuthState("session_invalid");
  }
  return true;
}

function isTerminalRefreshError(error: unknown): boolean {
  if (!(error instanceof StaffSessionError)) {
    return false;
  }
  if (error.reason === "approval_pending") {
    return true;
  }
  return error instanceof StaffProfileLookupError && !error.retryable;
}

function applyRestoredSession(input: {
  readonly clearAuthState: (reason: AuthErrorReason | null) => void;
  readonly completeSignIn: (result: StaffSessionResult) => boolean;
  readonly result: StaffSessionResult | null;
  readonly setIsLoading: (isLoading: boolean) => void;
}) {
  if (input.result) {
    input.completeSignIn(input.result);
    return;
  }
  input.clearAuthState(null);
  input.setIsLoading(false);
}

/** Provides staff auth state from restored password sessions and completed SMS/password sign-ins. */
export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [authError, setAuthError] = useState<AuthErrorReason | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const nextFocusRefreshAtRef = useRef(0);
  const hasAuthStateRef = useRef(false);
  const pendingSignInRef = useRef(false);
  const originRef = useRef<StaffSessionOrigin | null>(null);
  const smsExpiresAtRef = useRef<number | null>(null);
  // Monotonic auth-generation counter: every accepted/cleared session bumps it
  // so callbacks from an earlier generation cannot replace the current identity.
  const authGenerationRef = useRef(0);
  const activeProfileIdRef = useRef<string | null>(null);
  const activeAuthUserIdRef = useRef<string | null>(null);
  const refreshRunner = useMemo(() => createRefreshRunner(), []);

  useEffect(() => {
    hasAuthStateRef.current = Boolean(session || profile);
    pendingSignInRef.current = false;
    activeAuthUserIdRef.current = session?.user.id ?? null;
  }, [session, profile]);

  const clearAuthState = useCallback((reason: AuthErrorReason | null) => {
    authGenerationRef.current += 1;
    hasAuthStateRef.current = false;
    pendingSignInRef.current = false;
    // Cached business data must be gone before any other identity can render.
    if (activeProfileIdRef.current) {
      getQueryClient().clear();
    }
    activeProfileIdRef.current = null;
    activeAuthUserIdRef.current = null;
    originRef.current = null;
    smsExpiresAtRef.current = null;
    removeStoredSmsSession();
    // Touching `insforge` lazily initializes the client and throws when backend
    // config is absent. With no config there is no client or token to clear, so
    // skip it and keep local reset total.
    if (peekBackendConfig()) {
      insforge.setAccessToken(null);
    }
    setSession(null);
    setProfile(null);
    setAuthError(reason);
    clearUser();
  }, []);

  const completeStaffSignIn = useCompleteStaffSignIn({
    activeProfileIdRef,
    authGenerationRef,
    clearAuthState,
    hasAuthStateRef,
    pendingSignInRef,
    originRef,
    setAuthError,
    setIsLoading,
    setProfile,
    setSession,
    smsExpiresAtRef,
  });
  const completeSignIn = useCallback(
    (result: StaffSessionResult) => {
      cancelPasswordSessionRestores();
      const accepted = completeStaffSignIn(result);
      if (accepted) {
        nextFocusRefreshAtRef.current = Date.now() + FOCUS_FRESHNESS_MS;
        activeAuthUserIdRef.current = result.user.id;
      }
      return accepted;
    },
    [completeStaffSignIn],
  );
  useSmsSessionExpiry({
    authGenerationRef,
    clearAuthState,
    originRef,
    session,
  });

  // Capture the stable auth callbacks in refs so the bootstrap effect depends
  // only on stable values and registers its listeners exactly once.
  const completeSignInRef = useRef(completeSignIn);
  const clearAuthStateRef = useRef(clearAuthState);

  useEffect(() => {
    let cancelled = false;
    const completeStaffSignIn = completeSignInRef.current;
    const clearStaffAuthState = clearAuthStateRef.current;

    function handleRefreshError(error: unknown) {
      // A transport or upstream failure cannot prove that the current password
      // session is invalid. Keep an established identity until InsForge confirms
      // absence (the normal null result) or the approval gate denies it.
      if (hasAuthStateRef.current && !isTerminalRefreshError(error)) {
        return;
      }
      if (error instanceof StaffSessionError) {
        clearStaffAuthState(error.reason);
        return;
      }
      clearStaffAuthState("session_invalid");
    }

    async function runRefresh() {
      if (!refreshRunner.start(runRefresh)) {
        return;
      }
      if (pendingSignInRef.current) {
        refreshRunner.finish();
        return;
      }
      if (
        handleSmsRefresh({
          clearAuthState: clearStaffAuthState,
          expiresAt: smsExpiresAtRef.current,
          hasAuthState: hasAuthStateRef.current,
          origin: originRef.current,
        })
      ) {
        refreshRunner.finish();
        return;
      }
      const generation = authGenerationRef.current;
      const isCurrent = () => !cancelled && generation === authGenerationRef.current;
      nextFocusRefreshAtRef.current = Date.now() + FOCUS_RETRY_MS;
      try {
        const result = await restoreStaffSession(
          originRef.current,
          activeAuthUserIdRef.current,
          isCurrent,
        );
        if (cancelled || generation !== authGenerationRef.current) {
          // A logout or identity replacement happened while this refresh was in
          // flight — discard the stale result instead of restoring it.
          return;
        }
        applyRestoredSession({
          clearAuthState: clearStaffAuthState,
          completeSignIn: completeStaffSignIn,
          result,
          setIsLoading,
        });
      } catch (error) {
        if (!isCurrent() || error instanceof PasswordSessionRestoreCancelledError) {
          return;
        }
        nextFocusRefreshAtRef.current = Date.now() + FOCUS_RETRY_MS;
        handleRefreshError(error);
        setIsLoading(false);
      } finally {
        refreshRunner.finish();
      }
    }

    function handleFocus() {
      if (Date.now() >= nextFocusRefreshAtRef.current) {
        void runRefresh();
      }
    }

    refreshRunner.setLatest(runRefresh);
    void runRefresh();
    const intervalId = window.setInterval(runRefresh, TOKEN_REFRESH_MS);
    window.addEventListener("focus", handleFocus);

    return () => {
      cancelled = true;
      refreshRunner.clearLatest(runRefresh);
      window.removeEventListener("focus", handleFocus);
      clearInterval(intervalId);
    };
  }, [refreshRunner]);

  const signOut = useCallback(async () => {
    const origin = originRef.current;
    let remoteRevocationFailed = false;
    try {
      if (origin !== "sms") {
        await signOutPasswordSession();
      }
    } catch {
      // Remote revocation failure does not change the local boundary: clear
      // credentials and cached business data before completing logout.
      remoteRevocationFailed = true;
    }
    clearAuthState(null);
    setIsLoading(false);
    if (remoteRevocationFailed) {
      toast.warning(
        "Signed out on this device, but we couldn't revoke the remote session. Sign in again and retry if this device is shared.",
      );
      trackAction("auth.logout.remote_revocation_failed");
    }
    trackAction("auth.logout");
  }, [clearAuthState]);

  const approvalStatus = profile?.approval_status ?? null;
  const isApproved = isApprovedProfile(profile);

  const value = useMemo<AuthContextValue>(
    () => ({
      approvalStatus,
      authError,
      completeSignIn,
      isApproved,
      isLoading,
      profile,
      session,
      signOut,
      user: session?.user ?? null,
    }),
    [approvalStatus, authError, completeSignIn, isApproved, isLoading, profile, session, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Reads the current staff auth context; callers must be rendered under `AuthProvider`. */
export function useAuth(): AuthContextValue {
  return useAuthContext();
}
